import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { sendPackageWebhook } from '../lib/webhook';

const { FiX, FiCheck, FiUser, FiMail, FiMessageSquare, FiPhone } = FiIcons;

const PackageRequest = ({ isOpen, onClose, packageName }) => {
  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    email: '',
    primary_telephone: '',
    message: '',
    gdpr_consent: false
  });

  const [submitStatus, setSubmitStatus] = useState({
    loading: false,
    success: false,
    error: null
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const openPrivacyPolicy = () => {
    window.open('#/datenschutz', 'datenschutz', 'width=800,height=600,scrollbars=yes');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus({ loading: true, success: false, error: null });

    try {
      const appointmentData = {
        // Standardisierte Variablen für Make/Boost.space
        vorname: formData.vorname,
        nachname: formData.nachname,
        name: `${formData.vorname} ${formData.nachname}`.trim(),
        email: formData.email,
        primary_telephone: formData.primary_telephone,
        notes: formData.message || null,
        package_type: packageName,
        package_name: packageName,
        gdpr_consent: formData.gdpr_consent,
        
        // Zusätzliche Metadaten
        request_type: 'package_request',
        source_section: 'pricing_modal',
        lead_source: 'website_package_request',
        contact_reason: `${packageName}_package_interest`
      };

      // Send webhook data with GDPR compliance
      await sendPackageWebhook(appointmentData);

      // Set success
      setSubmitStatus({
        loading: false,
        success: true,
        error: null
      });

      // Reset form after submission
      setTimeout(() => {
        setFormData({
          vorname: '',
          nachname: '',
          email: '',
          primary_telephone: '',
          message: '',
          gdpr_consent: false
        });
        onClose();
        setSubmitStatus({ loading: false, success: false, error: null });
      }, 3000);

    } catch (error) {
      console.error('Error submitting package request:', error);
      setSubmitStatus({
        loading: false,
        success: false,
        error: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.'
      });
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-xl p-6 w-full max-w-md relative"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              disabled={submitStatus.loading}
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </button>

            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">
                {packageName} Paket anfragen
              </h3>
              <p className="text-gray-600">
                Füllen Sie das Formular aus, und wir melden uns in Kürze bei Ihnen.
              </p>
            </div>

            {submitStatus.success ? (
              <div className="text-center py-8">
                <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiCheck} className="text-green-500 text-4xl" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Anfrage erfolgreich gesendet!</h4>
                <p className="text-gray-600">
                  Vielen Dank für Ihr Interesse. Wir werden uns innerhalb von 24 Stunden bei Ihnen melden.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {submitStatus.error && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded">
                    <p className="text-red-700 text-sm">{submitStatus.error}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <SafeIcon icon={FiUser} className="inline mr-2" />
                      Vorname *
                    </label>
                    <input
                      type="text"
                      name="vorname"
                      value={formData.vorname}
                      onChange={handleInputChange}
                      required
                      disabled={submitStatus.loading}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                      placeholder="Ihr Vorname"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <SafeIcon icon={FiUser} className="inline mr-2" />
                      Nachname *
                    </label>
                    <input
                      type="text"
                      name="nachname"
                      value={formData.nachname}
                      onChange={handleInputChange}
                      required
                      disabled={submitStatus.loading}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                      placeholder="Ihr Nachname"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <SafeIcon icon={FiMail} className="inline mr-2" />
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={submitStatus.loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                    placeholder="ihre@email.de"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <SafeIcon icon={FiPhone} className="inline mr-2" />
                    Telefonnummer *
                  </label>
                  <input
                    type="tel"
                    name="primary_telephone"
                    value={formData.primary_telephone}
                    onChange={handleInputChange}
                    required
                    disabled={submitStatus.loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                    placeholder="0123 456789"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <SafeIcon icon={FiMessageSquare} className="inline mr-2" />
                    Nachricht
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    disabled={submitStatus.loading}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                    placeholder="Haben Sie spezielle Anforderungen oder Fragen?"
                  />
                </div>

                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    name="gdpr_consent"
                    checked={formData.gdpr_consent}
                    onChange={handleInputChange}
                    required
                    disabled={submitStatus.loading}
                    className="mt-1 w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-600">
                    Ich stimme der Verarbeitung meiner Daten gemäß der{' '}
                    <button
                      type="button"
                      onClick={openPrivacyPolicy}
                      className="text-red-500 hover:underline"
                    >
                      Datenschutzerklärung
                    </button>{' '}
                    zu und bin damit einverstanden, dass meine Angaben zur Bearbeitung meiner Paketanfrage gespeichert und verarbeitet werden. *
                  </span>
                </div>

                <div className="pt-4">
                  <motion.button
                    type="submit"
                    disabled={submitStatus.loading}
                    whileHover={{ scale: submitStatus.loading ? 1 : 1.03 }}
                    whileTap={{ scale: submitStatus.loading ? 1 : 0.97 }}
                    className={`w-full ${
                      submitStatus.loading ? 'bg-gray-400' : 'bg-red-500 hover:bg-red-600'
                    } text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center`}
                  >
                    {submitStatus.loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Wird gesendet...</span>
                      </>
                    ) : (
                      'Paket anfragen'
                    )}
                  </motion.button>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PackageRequest;